import React, { useState, useEffect, useMemo } from 'react';
import {
    Plus,
    Search,
    Edit2,
    Trash2,
    Users,
    Tag,
    Layers,
    Info,
    CheckCircle,
    XCircle,
    ChevronLeft,
    ChevronRight,
    Search as SearchIcon
} from 'lucide-react';
import {
    ButtonPrimary,
    ButtonSecondary,
    Card,
    Badge,
    Modal,
    Input,
    Alert,
    Spinner
} from '../../components/ComponentLibrary';
import {
    getGroups,
    createGroup,
    updateGroup,
    toggleGroupStatus
} from '../../services/api';
import './Admin.css';
import ConfirmModal from '../../components/ConfirmModal';


export default function AdminGroups() {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentGroupId, setCurrentGroupId] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        level: '',
        description: ''
    });

    // Confirm Modal state
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        type: 'danger',
        singleButton: false
    });

    const showAlert = (title, message, type = 'info') => {
        setConfirmModal({
            isOpen: true,
            title,
            message,
            type,
            singleButton: true,
            onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false }))
        });
    };


    // Pagination
    const [page, setPage] = useState(1);
    const pageSize = 8;

    const fetchGroups = async () => {
        try {
            setLoading(true);
            const res = await getGroups();
            setGroups(res.data);
            setError(null);
        } catch (err) {
            setError('Failed to fetch groups. Please try again.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchGroups();
    }, []);

    const filteredGroups = useMemo(() => {
        return groups.filter(g =>
            g.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            g.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (g.level && g.level.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }, [groups, searchTerm]);

    const paginatedGroups = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredGroups.slice(start, start + pageSize);
    }, [filteredGroups, page]);

    const totalPages = Math.ceil(filteredGroups.length / pageSize);

    const handleOpenModal = (group = null) => {
        if (group) {
            setIsEditing(true);
            setCurrentGroupId(group._id);
            setFormData({
                name: group.name,
                code: group.code,
                level: group.level || '',
                description: group.description || ''
            });
        } else {
            setIsEditing(false);
            setCurrentGroupId(null);
            setFormData({
                name: '',
                code: '',
                level: '',
                description: ''
            });
        }
        setIsModalOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) {
                await updateGroup(currentGroupId, formData);
            } else {
                await createGroup(formData);
            }
            setIsModalOpen(false);
            fetchGroups();
        } catch (err) {
            showAlert('Save Failed', err.message || 'Failed to save group', 'danger');
        }
    };


    const handleToggleStatus = (id) => {
        setConfirmModal({
            isOpen: true,
            title: 'Toggle Status',
            message: 'Are you sure you want to toggle this group status? Students in disabled groups may lose access to certain features.',
            type: 'warning',
            singleButton: false,
            onConfirm: async () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                try {
                    await toggleGroupStatus(id);
                    fetchGroups();
                } catch (err) {
                    showAlert('Update Failed', 'Failed to update group status', 'danger');
                }
            }
        });
    };


    if (loading && groups.length === 0) {
        return (
            <div className="admin-loading-state">
                <Spinner size="lg" />
                <p>Loading management systems...</p>
            </div>
        );
    }

    return (
        <div className="admin-groups-page finance-management-page">
            <div className="admin-page-header">
                <div className="header-text">
                    <h2 className="finance-main-title">🏷️ Groups & Job Codes</h2>
                    <p className="finance-subtitle">Manage student grouping and system-wide job classification codes.</p>
                </div>
                <div className="header-actions-group">
                    <ButtonPrimary onClick={() => handleOpenModal()}>
                        <Plus size={18} className="mr-2" /> Add New Group
                    </ButtonPrimary>
                </div>
            </div>

            {error && <Alert variant="error" className="mb-6">{error}</Alert>}

            <Card className="finance-card" hoverable={false}>
                <div className="card-header-flex">
                    <div className="card-title-group">
                        <div className="title-icon-wrapper">
                            <Users size={20} />
                        </div>
                        <div>
                            <h3>Active Groups</h3>
                            <p>All registered student groups and their codes</p>
                        </div>
                    </div>
                    <div className="table-filters">
                        <div className="search-box">
                            <SearchIcon size={16} />
                            <input
                                type="text"
                                placeholder="Search groups or codes..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>

                <div className="finance-table-wrapper">
                    <table className="finance-data-table">
                        <thead>
                            <tr>
                                <th>Group Name</th>
                                <th>Job Code</th>
                                <th>Level/Track</th>
                                <th>Status</th>
                                <th className="text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedGroups.length > 0 ? paginatedGroups.map(group => (
                                <tr key={group._id} className={!group.isActive ? 'opacity-50' : ''}>
                                    <td>
                                        <div className="group-info-cell">
                                            <div className="group-name-bold">{group.name}</div>
                                            <div className="group-desc-small text-muted">{group.description || 'No description'}</div>
                                        </div>
                                    </td>
                                    <td>
                                        <Badge variant="ocean" size="sm" className="font-mono">
                                            {group.code}
                                        </Badge>
                                    </td>
                                    <td>
                                        <div className="level-info">
                                            <Layers size={14} className="mr-1 inline text-muted" />
                                            {group.level || '---'}
                                        </div>
                                    </td>
                                    <td>
                                        {group.isActive ? (
                                            <Badge variant="success" size="sm">Active</Badge>
                                        ) : (
                                            <Badge variant="error" size="sm">Disabled</Badge>
                                        )}
                                    </td>
                                    <td>
                                        <div className="table-actions-row justify-end">
                                            <button
                                                className="table-action-btn edit"
                                                onClick={() => handleOpenModal(group)}
                                            >
                                                <Edit2 size={16} />
                                            </button>
                                            <button
                                                className={`table-action-btn ${group.isActive ? 'delete' : 'success'}`}
                                                onClick={() => handleToggleStatus(group._id)}
                                            >
                                                {group.isActive ? <XCircle size={16} /> : <CheckCircle size={16} />}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="5" className="empty-row">No groups found matching your criteria.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="table-pagination-footer">
                        <div className="pagination-info">
                            Page <strong>{page}</strong> of <strong>{totalPages}</strong>
                        </div>
                        <div className="pagination-btns">
                            <button
                                className="pagination-btn"
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                            >
                                <ChevronLeft size={18} />
                            </button>
                            <button
                                className="pagination-btn"
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page === totalPages}
                            >
                                <ChevronRight size={18} />
                            </button>
                        </div>
                    </div>
                )}
            </Card>

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={isEditing ? '📝 Edit Group' : '➕ Create New Group'}
                size="md"
            >
                <form onSubmit={handleSubmit} className="finance-modal-form">
                    <div className="form-grid">
                        <div className="full-width">
                            <Input
                                label="Group Name *"
                                placeholder="e.g. Frontend Beginners"
                                value={formData.name}
                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                required
                            />
                        </div>
                        <div>
                            <Input
                                label="Job Code *"
                                placeholder="e.g. FE-001"
                                value={formData.code}
                                onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                                required
                            />
                        </div>
                        <div>
                            <Input
                                label="Level / Track"
                                placeholder="e.g. Level 1"
                                value={formData.level}
                                onChange={e => setFormData({ ...formData, level: e.target.value })}
                            />
                        </div>
                        <div className="full-width">
                            <label className="cw-input-label">Description (Optional)</label>
                            <textarea
                                className="cw-input cw-textarea"
                                placeholder="Note about group focus or schedule..."
                                value={formData.description}
                                onChange={e => setFormData({ ...formData, description: e.target.value })}
                                rows="3"
                            />
                        </div>
                    </div>

                    <div className="modal-actions-footer">
                        <ButtonSecondary onClick={() => setIsModalOpen(false)} type="button">Cancel</ButtonSecondary>
                        <ButtonPrimary type="submit">{isEditing ? 'Update Group' : 'Create Group'}</ButtonPrimary>
                    </div>
                </form>
            </Modal>

            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                type={confirmModal.type}
                confirmText={confirmModal.singleButton ? "Ok" : "Confirm"}
                cancelText={confirmModal.singleButton ? null : "Cancel"}
                onConfirm={confirmModal.onConfirm}
                onCancel={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
            />
        </div>

    );
}
